import React, { useCallback, useState, useMemo } from 'react'
import ListTable from 'core/components/listTable/ListTable'
import useParams from 'core/hooks/useParams'
import { allKey } from 'app/constants'
import { profilesHelpUrl } from 'k8s/links'
import DateCell from 'core/components/listTable/cells/DateCell'
import SimpleLink from 'core/components/SimpleLink'
import { FormFieldCard } from 'core/components/validatedForm/FormFieldCard'
import { emptyArr } from 'utils/fp'
import useStyles from './useStyles'
import ApiPermissionsDialog, {
  ApiPermissionsParams,
} from 'k8s/components/rbac/profiles/create/ApiPermissionsDialog'
import useToggler from 'core/hooks/useToggler'
import ExpandIcon from 'k8s/components/rbac/profiles/ExpandIcon'
import { listClusterRoles } from 'k8s/components/rbac/new-actions'
import useListAction from 'core/hooks/useListAction'
import useSelectorWithParams from 'core/hooks/useSelectorWithParams'
import { clusterRolesSelector } from 'k8s/components/rbac/selectors'

const defaultParams = {
  masterNodeClusters: true,
  clusterId: allKey,
}
const visibleColumns = ['name', 'created', 'apiAccess']
const columnsOrder = ['name', 'created', 'apiAccess']
const orderBy = 'name'
const orderDirection = 'asc'
const searchTargets = ['name']

export const ClusterRolesListTable = ({
  data,
  onReload = null,
  loading = null,
  onSelectedRowsChange = null,
  selectedRows = emptyArr,
  ...rest
}) => {
  const [currentApiPermissions, setCurrentApiPermissions] = useState<ApiPermissionsParams>()
  const [showingPermissionsDialog, togglePermissionsDialog] = useToggler()
  const showApiPermissions = useCallback((row) => {
    setCurrentApiPermissions({
      name: row.name,
      cluster: row.clusterName,
      namespace: row.namespace,
      rules: row.rules,
    })
    togglePermissionsDialog()
  }, [])
  const columns = useMemo(
    () => [
      { id: 'id', display: false },
      { id: 'name', label: 'Name' },
      { id: 'created', label: 'Created', render: (value) => <DateCell value={value} /> },
      {
        id: 'apiAccess',
        render: (value, row) => (
          <ExpandIcon
            onClick={(e) => {
              e.stopPropagation()
              showApiPermissions(row)
            }}
          />
        ),
      },
    ],
    [],
  )

  return (
    <>
      <ApiPermissionsDialog
        onClose={togglePermissionsDialog}
        open={showingPermissionsDialog}
        name={currentApiPermissions?.name}
        cluster={currentApiPermissions?.cluster}
        rules={currentApiPermissions?.rules}
      />
      <ListTable
        uniqueIdentifier={'id'}
        canEditColumns={false}
        searchTargets={searchTargets}
        data={data}
        onReload={onReload}
        loading={loading}
        columns={columns}
        visibleColumns={visibleColumns}
        columnsOrder={columnsOrder}
        orderBy={orderBy}
        orderDirection={orderDirection}
        onSelectedRowsChange={onSelectedRowsChange}
        showCheckboxes={!!onSelectedRowsChange}
        selectedRows={selectedRows}
        wrapToolbar
        {...rest}
      />
    </>
  )
}

const ClusterRolesForm = ({ wizardContext, setWizardContext }) => {
  const classes = useStyles()
  const [selectedRows, setSelectedRows] = useState(emptyArr)
  const { params } = useParams(defaultParams)

  const { loading, reload } = useListAction(listClusterRoles, {
    params: { ...params, clusterId: wizardContext.baseCluster },
    requiredParams: ['clusterId'],
  })
  const data = useSelectorWithParams(clusterRolesSelector, {
    clusterId: wizardContext.baseCluster,
    useGlobalParams: false,
  })

  const refetch = useCallback(() => reload(true), [reload])
  const handleSelect = useCallback((rows) => {
    setSelectedRows(rows)
    setWizardContext({
      clusterRoles: rows.reduce((acc, row) => {
        acc[row.id] = row
        return acc
      }, {}),
    })
  }, [])

  return (
    <>
      <FormFieldCard
        className={classes.listBox}
        title={'RBAC Profile - Cluster Roles'}
        link={<SimpleLink src={profilesHelpUrl}>Profiles Help</SimpleLink>}
      >
        <ClusterRolesListTable
          data={data}
          onReload={refetch}
          loading={loading}
          onSelectedRowsChange={handleSelect}
          selectedRows={selectedRows}
          selectColumns={false}
        />
      </FormFieldCard>
    </>
  )
}

export default ClusterRolesForm
